import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { formatInTimeZone } from "date-fns-tz";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULTS = {
  language: "pt-BR",
  timezone: "America/Sao_Paulo",
  date_format: "dd/MM/yyyy",
  notification_prefs: {},
};

const PreferencesContext = createContext(DEFAULTS);

export function PreferencesProvider({ children }) {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(() => {
    try {
      const saved = localStorage.getItem("pipe4_user_prefs");
      return saved ? { ...DEFAULTS, ...JSON.parse(saved) } : DEFAULTS;
    } catch { return DEFAULTS; }
  });

  const fetchPrefs = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/user/preferences`, { withCredentials: true });
      const merged = { ...DEFAULTS, ...res.data };
      setPrefs(merged);
      localStorage.setItem("pipe4_user_prefs", JSON.stringify(merged));
      // Apply language
      if (merged.language) {
        const i18n = (await import("../i18n")).default;
        i18n.changeLanguage(merged.language);
        localStorage.setItem("pipe4_language", merged.language);
      }
    } catch { /* use cached */ }
  }, [user]);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const updatePrefs = async (newPrefs) => {
    const merged = { ...prefs, ...newPrefs };
    setPrefs(merged);
    localStorage.setItem("pipe4_user_prefs", JSON.stringify(merged));
    if (newPrefs.language) {
      const i18n = (await import("../i18n")).default;
      i18n.changeLanguage(newPrefs.language);
      localStorage.setItem("pipe4_language", newPrefs.language);
    }
    try {
      await axios.put(`${API}/user/preferences`, merged, { withCredentials: true });
    } catch { console.error("Failed to save preferences"); }
    return merged;
  };

  // Date formatter using user timezone
  const fmtDate = (iso, fmt) => {
    if (!iso) return "—";
    try {
      const tz = prefs.timezone || "America/Sao_Paulo";
      const format = fmt || prefs.date_format || "dd/MM/yyyy";
      return formatInTimeZone(new Date(iso), tz, format);
    } catch { return String(iso).slice(0, 10); }
  };

  const fmtDateTime = (iso) => fmtDate(iso, (prefs.date_format || "dd/MM/yyyy") + " HH:mm");

  const fmtCurrency = (value, symbol) => {
    if (value === null || value === undefined) return "—";
    const s = symbol || "R$";
    const locale = prefs.language === "en-US" ? "en-US" : "pt-BR";
    return `${s} ${new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value) || 0)}`;
  };

  return (
    <PreferencesContext.Provider value={{ prefs, updatePrefs, fmtDate, fmtDateTime, fmtCurrency }}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences() {
  return useContext(PreferencesContext);
}
